import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Plus, Trash2, ExternalLink } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';

export default function ShoppingList() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [name, setName] = useState('');
  const [qty, setQty] = useState('');

  const load = () => api.get('/shopping-list').then(({ data }) => setItems(data));

  useEffect(() => { if (user) load(); }, [user]);

  const add = async () => {
    if (!name.trim()) return;
    await api.post('/shopping-list', { name: name.trim(), qty });
    setName(''); setQty(''); load();
  };

  const toggle = async (it) => {
    await api.patch(`/shopping-list/${it.item_id}`, { bought: !it.bought });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/shopping-list/${id}`);
    load();
  };

  if (!user) {
    return (
      <div className="text-center py-20 space-y-4" data-testid="shopping-page">
        <ShoppingBag className="h-12 w-12 mx-auto text-[#C05A42]" />
        <p className="text-[#5C4D44]">{t('needLogin')}</p>
        <Link to="/login" className="inline-flex items-center rounded-full bg-[#C05A42] text-white px-6 py-3 text-sm font-medium hover:bg-[#A04832] transition" data-testid="login-link">{t('loginWithGoogle')}</Link>
      </div>
    );
  }

  const itemsList = items.map(it => `${it.qty} ${it.name}`).join(', ');
  const orderUrl = `https://www.google.com/search?q=${encodeURIComponent('grocery delivery ' + itemsList)}`;

  return (
    <div className="space-y-6" data-testid="shopping-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{t('shopping')}</h1>
        {items.length > 0 && (
          <a href={orderUrl} target="_blank" rel="noreferrer" data-testid="order-online-link"
             className="inline-flex items-center gap-1 rounded-full bg-[#7A8B76] hover:bg-[#62725f] text-white px-5 py-2.5 text-sm font-medium transition">
            <ExternalLink className="h-4 w-4" /> {t('onlineShop')}
          </a>
        )}
      </div>

      <div className="rounded-3xl bg-white border border-black/5 p-5 flex gap-2 flex-wrap">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('itemName')} className="flex-1 min-w-[140px]" data-testid="shopping-item-name" />
        <Input value={qty} onChange={(e) => setQty(e.target.value)} placeholder={t('qty')} className="w-24" data-testid="shopping-item-qty" />
        <Button onClick={add} data-testid="shopping-add-btn" className="rounded-full bg-[#C05A42] hover:bg-[#A04832] text-white">
          <Plus className="h-4 w-4 me-1" /> {t('addItem')}
        </Button>
      </div>

      {items.length === 0 ? (
        <p className="text-[#5C4D44] text-center py-12">{t('shoppingEmpty')}</p>
      ) : (
        <ul className="space-y-2" data-testid="shopping-items">
          {items.map((it) => (
            <li key={it.item_id} className={`flex items-center gap-3 rounded-2xl bg-white border border-black/5 p-4 transition ${it.bought ? 'opacity-50' : ''}`}>
              <Checkbox checked={it.bought} onCheckedChange={() => toggle(it)} data-testid={`shopping-check-${it.item_id}`} />
              <div className={`flex-1 ${it.bought ? 'line-through' : ''}`}>
                <span className="font-medium text-[#2C2420]">{it.name}</span>
                {it.qty && <span className="ms-2 text-sm text-[#5C4D44]">— {it.qty}</span>}
              </div>
              <button onClick={() => remove(it.item_id)} data-testid={`shopping-delete-${it.item_id}`} className="text-[#5C4D44] hover:text-[#C05A42]">
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
